/**
 * Collection Filters JavaScript
 * Handles desktop dropdown filters and mobile drawer interactions
 */

class CollectionFilters {
  constructor() {
    this.init();
  }

  init() {
    this.setupDesktopFilters();
    this.setupMobileDrawer();
    this.setupSortBy();
    this.setupFormSubmission();
  }

  /**
   * Desktop Filter Dropdowns
   */
  setupDesktopFilters() {
    const filterToggles = document.querySelectorAll('[data-filter-toggle]');

    filterToggles.forEach(toggle => {
      const dropdown = toggle.nextElementSibling;
      if (!dropdown) return;

      // Toggle dropdown on button click
      toggle.addEventListener('click', e => {
        e.stopPropagation();
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        // Close all other dropdowns
        this.closeAllDropdowns();

        // Toggle current dropdown
        if (!isExpanded) {
          toggle.setAttribute('aria-expanded', 'true');
          dropdown.classList.remove('hidden');
        }
      });

      // Handle checkbox changes (desktop only - auto-submit)
      const checkboxes = dropdown.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          // Auto-submit form on change (progressive enhancement)
          const form = checkbox.closest('form');
          if (form && !form.hasAttribute('data-mobile-filters-form')) {
            this.submitForm(form);
          }
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', e => {
      if (!e.target.closest('[data-filter-group]')) {
        this.closeAllDropdowns();
      }
    });

    // Handle show more buttons
    const showMoreButtons = document.querySelectorAll('[data-show-more]');
    showMoreButtons.forEach(button => {
      button.addEventListener('click', () => {
        const filterGroup = button.closest('[data-filter-dropdown]');
        const hiddenItems = filterGroup.querySelectorAll('li:nth-child(n+11)');
        hiddenItems.forEach(item => item.classList.remove('hidden'));
        button.parentElement.remove();
      });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  closeAllDropdowns() {
    const allToggles = document.querySelectorAll('[data-filter-toggle]');
    allToggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
      const dropdown = toggle.nextElementSibling;
      if (dropdown) {
        dropdown.classList.add('hidden');
      }
    });
  }

  /**
   * Mobile Filter Drawer
   */
  setupMobileDrawer() {
    const drawerTrigger = document.querySelector(
      '[data-filter-drawer-trigger]'
    );
    const drawer = document.querySelector('[data-filter-drawer]');
    const overlay = document.querySelector('[data-filter-drawer-overlay]');
    const closeButton = document.querySelector('[data-filter-drawer-close]');
    const applyButton = document.querySelector('[data-filter-drawer-apply]');

    if (!drawerTrigger || !drawer || !overlay) return;

    // Open drawer
    drawerTrigger.addEventListener('click', () => {
      drawer.classList.remove('-translate-x-full');
      overlay.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    });

    // Close drawer
    const closeDrawer = () => {
      drawer.classList.add('-translate-x-full');
      overlay.classList.add('hidden');
      document.body.style.overflow = '';
    };

    closeButton?.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);

    // Apply filters
    applyButton?.addEventListener('click', () => {
      const form = document.querySelector('[data-mobile-filters-form]');
      if (form) {
        this.submitMobileForm(form);
      }
      closeDrawer();
    });

    // Handle details/summary for collapsible filter groups
    const details = drawer.querySelectorAll('details');
    details.forEach(detail => {
      const summary = detail.querySelector('summary');
      const chevron = summary?.querySelector('svg');

      // Set initial state
      if (chevron && detail.open) {
        chevron.style.transform = 'rotate(180deg)';
      }

      summary?.addEventListener('click', () => {
        // Toggle chevron rotation
        if (chevron) {
          setTimeout(() => {
            chevron.style.transform = detail.open
              ? 'rotate(180deg)'
              : 'rotate(0deg)';
          }, 0);
        }
      });
    });

    // Handle clear filter buttons
    const clearButtons = drawer.querySelectorAll('[data-clear-filter]');
    clearButtons.forEach(button => {
      button.addEventListener('click', () => {
        const filterName = button.getAttribute('data-clear-filter');
        const checkboxes = drawer.querySelectorAll(
          `input[name^="${filterName}"]`
        );
        checkboxes.forEach(checkbox => {
          // eslint-disable-next-line no-param-reassign
          checkbox.checked = false;
        });
      });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  submitMobileForm(form) {
    const url = new URL(window.location.href);

    // Clear existing filter parameters
    const keysToDelete = [];
    url.searchParams.forEach((_value, key) => {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => url.searchParams.delete(key));

    // Get all checked checkboxes from mobile form
    const checkedInputs = form.querySelectorAll(
      'input[type="checkbox"]:checked'
    );

    // Add each checked filter to URL
    checkedInputs.forEach(input => {
      if (input.name && input.value) {
        url.searchParams.append(input.name, input.value);
      }
    });

    // Navigate to new URL
    window.location.href = url.toString();
  }

  // eslint-disable-next-line class-methods-use-this
  syncMobileToDesktopForm(mobileForm, desktopForm) {
    // Get all checked checkboxes from mobile form
    const mobileInputs = mobileForm.querySelectorAll(
      'input[type="checkbox"]:checked'
    );
    const mobileNumberInputs = mobileForm.querySelectorAll(
      'input[type="number"]'
    );

    // Clear desktop form selections
    const desktopCheckboxes = desktopForm.querySelectorAll(
      'input[type="checkbox"]'
    );
    desktopCheckboxes.forEach(cb => {
      // eslint-disable-next-line no-param-reassign
      cb.checked = false;
    });

    // Apply mobile selections to desktop
    mobileInputs.forEach(input => {
      const desktopInput = desktopForm.querySelector(
        `input[name="${input.name}"][value="${input.value}"]`
      );
      if (desktopInput) {
        desktopInput.checked = true;
      }
    });

    // Sync number inputs (price range)
    mobileNumberInputs.forEach(input => {
      const desktopInput = desktopForm.querySelector(
        `input[name="${input.name}"]`
      );
      if (desktopInput) {
        desktopInput.value = input.value;
      }
    });
  }

  /**
   * Sort By Dropdown
   */
  // eslint-disable-next-line class-methods-use-this
  setupSortBy() {
    const sortOptions = document.querySelectorAll('[data-sort-option]');
    const sortDrawerTrigger = document.querySelector(
      '[data-sort-drawer-trigger]'
    );

    // Handle sort option clicks
    sortOptions.forEach(option => {
      option.addEventListener('click', e => {
        e.preventDefault();
        const sortValue = option.getAttribute('data-sort-option');

        // Preserve existing query parameters
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortValue);
        window.location.href = url.toString();
      });
    });

    // Mobile sort drawer (simplified for now)
    if (sortDrawerTrigger) {
      sortDrawerTrigger.addEventListener('click', () => {
        // You can implement a sort drawer similar to filter drawer if needed
        // eslint-disable-next-line no-console
        console.warn('Sort functionality - implement drawer if needed');
      });
    }
  }

  /**
   * Form Submission Handler
   */
  setupFormSubmission() {
    const forms = document.querySelectorAll('[data-collection-filters-form]');

    forms.forEach(form => {
      form.addEventListener('submit', e => {
        e.preventDefault();
        this.submitForm(form);
      });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  submitForm(form) {
    const formData = new FormData(form);
    const url = new URL(window.location.href);

    // Clear existing filter parameters
    const keysToDelete = [];
    url.searchParams.forEach((_value, key) => {
      if (key.startsWith('filter.') || key === 'sort_by') {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => url.searchParams.delete(key));

    // Add form data to URL (only non-empty values)
    formData.forEach((value, key) => {
      if (value && value.trim() !== '') {
        url.searchParams.append(key, value);
      }
    });

    // Navigate to new URL
    window.location.href = url.toString();
  }
}

// Initialize when DOM is ready
function initializeFilters() {
  return new CollectionFilters();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFilters);
} else {
  initializeFilters();
}
